import { useState } from 'react';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  Botao,
  CabecalhoPagina,
  CampoTexto,
  classeTd,
  classeTh,
  Etiqueta,
  EstadoVazio,
  OpcoesRadio,
  Painel,
  Tabela,
} from '../components/ui/index.js';
import { useConsulta, useFonte } from '../dados/contexto.js';
import type { Haras, PessoaDaEquipe, Usuario } from '../dados/fonte.js';

const PAPEIS: readonly { valor: Usuario['papel']; rotulo: string; ajuda: string }[] = [
  { valor: 'avaliador', rotulo: 'Avaliador', ajuda: 'Cadastra, coleta, fecha fichas e registra emendas.' },
  { valor: 'admin', rotulo: 'Admin', ajuda: 'Tudo o que o avaliador faz, mais equipe, acessos, reabrir e validar fichas.' },
  { valor: 'haras', rotulo: 'Dono de haras', ajuda: 'Só lê o plantel dos haras que você liberar abaixo.' },
  { valor: 'leitor', rotulo: 'Leitor', ajuda: 'Só leitura, sem acesso a haras nenhum até ser liberado.' },
];
const ROTULO_PAPEL = Object.fromEntries(PAPEIS.map((p) => [p.valor, p.rotulo])) as Record<Usuario['papel'], string>;

/**
 * Equipe e acessos (so admin): quem usa o sistema, com qual papel, e quais haras
 * cada dono enxerga. A pessoa e cadastrada pelo e-mail; o login se liga a ela
 * sozinho no primeiro acesso.
 */
export function Equipe() {
  const fonte = useFonte();
  const consulta = useConsulta(async (f) => {
    const usuario = await f.usuarioAtual();
    if (!f.cadastro || usuario?.papel !== 'admin') return null;
    const [pessoas, haras] = await Promise.all([f.cadastro.equipe(), f.listarHaras()]);
    return { pessoas, haras, eu: usuario.id };
  }, []);
  const [editando, setEditando] = useState<PessoaDaEquipe | 'nova' | null>(null);

  if (!fonte.cadastro) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {(d) =>
        !d ? (
          <EstadoVazio titulo="Só o admin gerencia a equipe">Peça a um admin para cadastrar pessoas ou liberar acessos.</EstadoVazio>
        ) : (
          <div className="space-y-6">
            <CabecalhoPagina
              titulo="Equipe e acessos"
              meta={<span>Quem usa o Equiview, com qual papel, e quais haras cada dono enxerga.</span>}
              acoes={
                <Botao variante="principal" onClick={() => setEditando('nova')}>
                  Nova pessoa
                </Botao>
              }
            />

            {editando && (
              <FormPessoa
                key={editando === 'nova' ? 'nova' : editando.id}
                pessoa={editando === 'nova' ? null : editando}
                haras={d.haras}
                eu={d.eu}
                aoTerminar={() => setEditando(null)}
              />
            )}

            <Painel titulo={`Pessoas (${d.pessoas.length})`} corpo="p-0">
              <Tabela rotulo="Pessoas">
                <thead>
                  <tr>
                    <th scope="col" className={classeTh}>Nome</th>
                    <th scope="col" className={`${classeTh} hidden sm:table-cell`}>E-mail</th>
                    <th scope="col" className={classeTh}>Papel</th>
                    <th scope="col" className={`${classeTh} hidden md:table-cell`}>Situação</th>
                    <th scope="col" className={classeTh}>
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.pessoas.map((p) => (
                    <tr key={p.id}>
                      <td className={classeTd}>
                        <span className="font-medium">{p.nome}</span>
                        {p.id === d.eu && <span className="text-xs text-apagado"> · você</span>}
                        {p.papel === 'haras' && (
                          <span className="block text-xs text-apagado">
                            {p.harasIds.length
                              ? d.haras.filter((h) => p.harasIds.includes(h.id)).map((h) => h.name).join(', ')
                              : 'Nenhum haras liberado'}
                          </span>
                        )}
                      </td>
                      <td className={`${classeTd} hidden text-apagado sm:table-cell`}>{p.email ?? '—'}</td>
                      <td className={classeTd}>
                        <Etiqueta tom={p.papel === 'admin' ? 'marca' : 'neutro'}>{ROTULO_PAPEL[p.papel]}</Etiqueta>
                      </td>
                      <td className={`${classeTd} hidden md:table-cell`}>
                        {!p.ativo ? (
                          <Etiqueta tom="aviso">Inativa</Etiqueta>
                        ) : p.vinculada ? (
                          <span className="text-sm text-ok">Já entrou</span>
                        ) : (
                          <span className="text-sm text-apagado">Aguardando o primeiro acesso</span>
                        )}
                      </td>
                      <td className={`${classeTd} text-right`}>
                        <Botao tamanho="sm" variante="discreto" onClick={() => setEditando(p)}>
                          Editar
                        </Botao>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
            </Painel>
          </div>
        )
      }
    </Aguardar>
  );
}

function FormPessoa({
  pessoa,
  haras,
  eu,
  aoTerminar,
}: {
  pessoa: PessoaDaEquipe | null;
  haras: readonly Haras[];
  eu: string;
  aoTerminar: () => void;
}) {
  const cadastro = useFonte().cadastro!;
  const [nome, setNome] = useState(pessoa?.nome ?? '');
  const [email, setEmail] = useState(pessoa?.email ?? '');
  const [papel, setPapel] = useState<Usuario['papel']>(pessoa?.papel ?? 'avaliador');
  const [ativo, setAtivo] = useState(pessoa?.ativo ?? true);
  const [acessos, setAcessos] = useState<Set<string>>(new Set(pessoa?.harasIds ?? []));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const souEu = pessoa?.id === eu;

  const salvar = async () => {
    setErro(null);
    setSalvando(true);
    try {
      const id = await cadastro.salvarPessoa({ ...(pessoa ? { id: pessoa.id } : {}), full_name: nome, email, app_role: papel, active: ativo });
      // Acesso a haras so faz sentido para quem nao e da equipe (a equipe ve tudo).
      await cadastro.definirAcessos(id, papel === 'haras' || papel === 'leitor' ? [...acessos] : []);
      aoTerminar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Painel titulo={pessoa ? `Editar · ${pessoa.nome}` : 'Nova pessoa'}>
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoTexto rotulo="Nome" obrigatorio value={nome} onChange={(e) => setNome(e.target.value)} />
          <CampoTexto
            rotulo="E-mail"
            obrigatorio
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            ajuda="A pessoa entra com este e-mail; o login se liga a ela no primeiro acesso."
          />
        </div>
        <OpcoesRadio
          rotulo="Papel"
          valor={papel}
          onMudar={(v) => !souEu && setPapel(v)}
          opcoes={PAPEIS.map((p) => ({ valor: p.valor, rotulo: p.rotulo }))}
          ajuda={souEu ? 'Você não pode mudar o seu próprio papel.' : PAPEIS.find((p) => p.valor === papel)?.ajuda}
        />
        {(papel === 'haras' || papel === 'leitor') && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Haras que esta pessoa enxerga</legend>
            {haras.length === 0 ? (
              <p className="text-sm text-apagado">Nenhum haras cadastrado ainda.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {haras.map((h) => (
                  <label key={h.id} className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={acessos.has(h.id)}
                      onChange={(e) => {
                        const novo = new Set(acessos);
                        if (e.target.checked) novo.add(h.id);
                        else novo.delete(h.id);
                        setAcessos(novo);
                      }}
                    />
                    {h.name}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}
        {pessoa && !souEu && (
          <OpcoesRadio
            rotulo="Situação"
            valor={ativo ? 'ativa' : 'inativa'}
            onMudar={(v) => setAtivo(v === 'ativa')}
            opcoes={[
              { valor: 'ativa', rotulo: 'Ativa' },
              { valor: 'inativa', rotulo: 'Inativa' },
            ]}
            ajuda="Inativa não entra mais. O histórico dela (medidas, emendas) continua."
          />
        )}
        {erro && <p className="text-sm text-erro">{erro}</p>}
        <div className="flex gap-2">
          <Botao variante="principal" carregando={salvando} disabled={!nome.trim() || !email.trim()} onClick={() => void salvar()}>
            Salvar
          </Botao>
          <Botao variante="discreto" onClick={aoTerminar}>
            Cancelar
          </Botao>
        </div>
      </div>
    </Painel>
  );
}
